// Sets <html lang>, document.title, theme-color, OpenGraph tags, the
// favicon, and the PWA manifest from the active industry config layered
// with the runtime tenant brand. Pages override the title locally via
// useDocumentMeta.
import { useEffect } from 'react';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import {
  buildRuntimeManifest,
  readBaseManifest,
  applyRuntimeManifest,
} from '@/pwa/runtimeManifest';
import i18n from '@/i18n';
import { useIndustry } from './industry-context.js';

function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function setMetaContent(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProperty(prop, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', prop);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLinkHref(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  if (rel === 'icon') {
    el.removeAttribute('type');
  }
  el.setAttribute('href', href);
}

// Read once: the build-time manifest never changes within a session, and
// re-reading it after the first swap would fetch our own blob back.
let baseManifestPromise = null;
function getBaseManifest() {
  baseManifestPromise ??= readBaseManifest();
  return baseManifestPromise;
}

export default function DocumentHead() {
  const { config } = useIndustry();
  const { brandName, logoUrl } = useRuntimeBrand();

  useEffect(() => {
    const activeLanguage = String(i18n.resolvedLanguage || i18n.language || config?.locale || 'en')
      .split('-')[0]
      .toLowerCase();
    document.documentElement.setAttribute('lang', activeLanguage);
    document.documentElement.setAttribute('dir', activeLanguage === 'ar' ? 'rtl' : 'ltr');
    const title = brandName || config?.copy?.appName || config?.name || '';
    const activeLogo = logoUrl || config?.logoUrl || '';
    document.title = title;

    const description = config?.copy?.tagline ?? '';
    setMetaContent('description', description);

    // theme-color is read at runtime via the active --color-primary CSS
    // variable so it always matches the active industry.
    const primary = cssVar('--color-primary');
    if (primary) setMetaContent('theme-color', primary);

    // OpenGraph + Twitter card. Industries can override og:image by
    // shipping their own asset; falls back to the shared brand image.
    setMetaProperty('og:title', title);
    setMetaProperty('og:description', description);
    setMetaProperty('og:type', 'website');
    setMetaProperty('og:image', activeLogo || '');
    setMetaContent('twitter:card', 'summary_large_image');
    setLinkHref('icon', activeLogo || '');
    setLinkHref('apple-touch-icon', activeLogo || '');
  }, [brandName, config, logoUrl]);

  // The OS install dialog reads the manifest, not the DOM above — so the
  // tenant name and logo have to be written into it separately. This hands the
  // manifest to the service worker, which serves it; it resolves to false where
  // no SW takes control (dev server), leaving the build-time manifest in place.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const baseManifest = await getBaseManifest();
      if (cancelled) return;

      await applyRuntimeManifest(
        buildRuntimeManifest({
          baseManifest: baseManifest ?? {},
          brandName: brandName || config?.copy?.appName || config?.name || '',
          logoUrl: logoUrl || config?.logoUrl || '',
          description: config?.copy?.tagline ?? '',
          themeColor: cssVar('--color-primary'),
          backgroundColor: cssVar('--runtime-color-background') || cssVar('--bg-surface'),
        }),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [brandName, config, logoUrl]);

  return null;
}
